// Settings lists for the operations modules (docs/plans/phase3-operations-modules.md §0.1,
// ADR 0036): incident categories and severities, isolation precautions, infection
// categories and asset categories. They are settings data, never code: an organisation
// changes them in Settings. Records copy the code and the display at capture, so a later
// rename never rewrites history (CLAUDE.md, domain rule 9).

import { err, ok, type Result } from '../result.ts';

export const OPERATIONS_LIST_KINDS = [
  'incident_category',
  'incident_severity',
  'isolation_precaution',
  'infection_category',
  'asset_category',
  'diet_type',
  'diet_texture',
] as const;
export type OperationsListKind = (typeof OPERATIONS_LIST_KINDS)[number];

export interface OperationsListItem {
  readonly code: string;
  readonly display: string;
}

export const MAX_LIST_DISPLAY_LENGTH = 80;
const CODE_PATTERN = /^[a-z0-9_]{1,40}$/;

export type OperationsListErrorCode =
  | 'operations_list.code_invalid'
  | 'operations_list.display_invalid'
  | 'operations_list.duplicate_code';

export const OPERATIONS_LIST_ERROR_MESSAGES: Record<OperationsListErrorCode, string> = {
  'operations_list.code_invalid':
    'Codes are lower-case letters, digits and underscores, up to 40 characters.',
  'operations_list.display_invalid': `Give each item a name of up to ${String(MAX_LIST_DISPLAY_LENGTH)} characters.`,
  'operations_list.duplicate_code': 'Each code can be in the list once.',
};

/** A code is fixed once saved; the display is trimmed and can be corrected later. */
export function checkListItem(
  item: OperationsListItem,
): Result<OperationsListItem, OperationsListErrorCode> {
  if (!CODE_PATTERN.test(item.code)) return err('operations_list.code_invalid');
  const display = item.display.trim();
  if (display === '' || display.length > MAX_LIST_DISPLAY_LENGTH)
    return err('operations_list.display_invalid');
  return ok({ code: item.code, display });
}

/** A whole list as saved from Settings: every item valid, each code once. */
export function checkList(
  items: readonly OperationsListItem[],
): Result<OperationsListItem[], OperationsListErrorCode> {
  const checked: OperationsListItem[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const result = checkListItem(item);
    if (!result.ok) return result;
    if (seen.has(result.value.code)) return err('operations_list.duplicate_code');
    seen.add(result.value.code);
    checked.push(result.value);
  }
  return ok(checked);
}

/**
 * The provisional lists every organisation starts with. TODO(clinical-verify): the incident
 * categories and the harm scale (plan I1) are for the clinical safety lead to confirm or
 * replace; they are settings data, so a facility can change them without a release.
 */
export const DEFAULT_OPERATIONS_LISTS: Record<OperationsListKind, readonly OperationsListItem[]> = {
  incident_category: [
    { code: 'patient_fall', display: 'Patient fall' },
    { code: 'medication_error', display: 'Medication error' },
    { code: 'clinical_care', display: 'Clinical care' },
    { code: 'infection_control', display: 'Infection control' },
    { code: 'equipment_failure', display: 'Equipment failure' },
    { code: 'security', display: 'Security' },
    { code: 'staff_injury', display: 'Staff injury' },
    { code: 'documentation', display: 'Documentation' },
    { code: 'other', display: 'Other' },
  ],
  // In order of harm: the rank is the position, 1 being the least.
  incident_severity: [
    { code: 'no_harm', display: 'No harm' },
    { code: 'low_harm', display: 'Low harm' },
    { code: 'moderate_harm', display: 'Moderate harm' },
    { code: 'severe_harm', display: 'Severe harm' },
    { code: 'death', display: 'Death' },
  ],
  // TODO(clinical-verify): plan C1. Precautions as the infection-control nurse names them.
  isolation_precaution: [
    { code: 'contact', display: 'Contact' },
    { code: 'droplet', display: 'Droplet' },
    { code: 'airborne', display: 'Airborne' },
    { code: 'protective', display: 'Protective (reverse)' },
  ],
  // TODO(clinical-verify): plan C1. Category names only; no surveillance definition is
  // applied by Wodi.
  infection_category: [
    { code: 'ssi', display: 'Surgical site infection' },
    { code: 'cauti', display: 'Catheter-associated urinary tract infection' },
    { code: 'clabsi', display: 'Central line-associated bloodstream infection' },
    { code: 'vap', display: 'Ventilator-associated pneumonia' },
    { code: 'hap', display: 'Hospital-acquired pneumonia' },
    { code: 'cdi', display: 'Clostridioides difficile infection' },
    { code: 'other', display: 'Other' },
  ],
  // Asset categories (plan §3.1): an operational list, not clinical.
  asset_category: [
    { code: 'medical_equipment', display: 'Medical equipment' },
    { code: 'lab_equipment', display: 'Lab equipment' },
    { code: 'it_equipment', display: 'IT equipment' },
    { code: 'furniture', display: 'Furniture' },
    { code: 'vehicle', display: 'Vehicle' },
    { code: 'plant', display: 'Plant and building services' },
    { code: 'other', display: 'Other' },
  ],
  // TODO(clinical-verify): kitchen plan K1. Diet names only, for the clinician to choose and
  // the kitchen to prepare; Wodi applies no clinical rule to any of them.
  diet_type: [
    { code: 'normal', display: 'Normal' },
    { code: 'soft', display: 'Soft' },
    { code: 'diabetic', display: 'Diabetic' },
    { code: 'low_salt', display: 'Low salt' },
    { code: 'high_protein', display: 'High protein' },
    { code: 'renal', display: 'Renal' },
    { code: 'liquid', display: 'Liquid' },
    { code: 'clear_fluids', display: 'Clear fluids' },
    { code: 'other', display: 'Other' },
  ],
  // TODO(clinical-verify): kitchen plan K2. Textures as the kitchen prepares them.
  diet_texture: [
    { code: 'regular', display: 'Regular' },
    { code: 'soft', display: 'Soft' },
    { code: 'minced', display: 'Minced' },
    { code: 'pureed', display: 'Pureed' },
    { code: 'liquidised', display: 'Liquidised' },
  ],
};

/** A severity's rank in its list (1 = least harm), or null when it isn't in the list. */
export function severityRankOf(
  severities: readonly OperationsListItem[],
  code: string,
): number | null {
  const index = severities.findIndex((s) => s.code === code);
  return index === -1 ? null : index + 1;
}
